import React, { useState, useEffect, useMemo } from 'react';
import {
  Compass,
  Film,
  Tv,
  Star,
  Calendar,
  Sparkles,
  Search,
  Filter,
  SlidersHorizontal,
  Play,
  Plus,
  Check,
  RotateCcw,
} from 'lucide-react';
import { api, type MediaItem, type MediaFile } from '../lib/api';
import { useThemeLanguage } from '../context/ThemeLanguageContext';

export interface ExplorePageProps {
  onOpenMedia: (id: string) => void;
  onPlay: (file: MediaFile, title: string, subtitle?: string, initialTime?: number) => void;
}

const GENRE_LIST = [
  'Action',
  'Comedy',
  'Drama',
  'Sci-Fi',
  'Horror',
  'Animation',
  'Thriller',
  'Adventure',
  'Fantasy',
  'Crime',
  'Romance',
  'Documentary',
];

const GENRE_TRANSLATIONS: Record<string, { he: string; en: string }> = {
  Action: { he: 'פעולה / אקשן', en: 'Action' },
  Comedy: { he: 'קומדיה', en: 'Comedy' },
  Drama: { he: 'דרמה', en: 'Drama' },
  'Sci-Fi': { he: 'מדע בדיוני', en: 'Sci-Fi' },
  Horror: { he: 'אימה', en: 'Horror' },
  Animation: { he: 'אנימציה', en: 'Animation' },
  Thriller: { he: 'מתח', en: 'Thriller' },
  Adventure: { he: 'הרפתקאות', en: 'Adventure' },
  Fantasy: { he: 'פנטזיה', en: 'Fantasy' },
  Crime: { he: 'פשע', en: 'Crime' },
  Romance: { he: 'רומנטיקה', en: 'Romance' },
  Documentary: { he: 'דוקומנטרי', en: 'Documentary' },
};

const MOOD_LIST = [
  { id: 'all', emoji: '✨', labelHe: 'כל האווירות', labelEn: 'All Vibes', genres: [] },
  { id: 'adrenaline', emoji: '⚡', labelHe: 'אדרנלין ומתח', labelEn: 'Action & Thrills', genres: ['action', 'adventure', 'thriller', 'crime'] },
  { id: 'funny', emoji: '🍿', labelHe: 'קליל ומצחיק', labelEn: 'Feel-Good & Comedy', genres: ['comedy', 'animation', 'family'] },
  { id: 'mindbend', emoji: '🧠', labelHe: 'מסתורין ומד״ב', labelEn: 'Mind-Benders', genres: ['sci-fi', 'science fiction', 'mystery', 'horror'] },
  { id: 'cozy', emoji: '🧸', labelHe: 'משפחתי ונוסטלגי', labelEn: 'Family & Cozy', genres: ['family', 'animation', 'adventure', 'fantasy'] },
  { id: 'romance', emoji: '💖', labelHe: 'רומנטיקה ודרמה', labelEn: 'Heartfelt & Romance', genres: ['romance', 'drama'] },
];

export function ExplorePage({ onOpenMedia, onPlay }: ExplorePageProps) {
  const { t, language, isRtl, showToast } = useThemeLanguage();
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterType, setFilterType] = useState<'all' | 'movie' | 'show'>('all');
  const [selectedMood, setSelectedMood] = useState<string>('all');
  const [selectedGenre, setSelectedGenre] = useState<string>('all');
  const [selectedYearRange, setSelectedYearRange] = useState<string>('all');
  const [minRating, setMinRating] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'popularity' | 'rating' | 'year' | 'title'>('popularity');
  const [watchlistIds, setWatchlistIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    Promise.all([
      api.listMedia({ limit: '100' }),
      api.getHomeFeed().catch(() => ({ watchlist: [] })),
    ])
      .then(([res, feed]) => {
        if (!isMounted) return;
        setItems(res.items || []);
        if (feed.watchlist) {
          setWatchlistIds(new Set(feed.watchlist.map((w: any) => w.id)));
        }
      })
      .catch((err) => console.error('Failed to load explore items', err))
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const toggleWatchlist = async (e: React.MouseEvent, item: MediaItem) => {
    e.stopPropagation();
    try {
      const res = await api.toggleWatchlist(item.id);
      if (res.inWatchlist) {
        setWatchlistIds((prev) => new Set(prev).add(item.id));
        showToast(language === 'he' ? 'נוסף לרשימה שלי' : 'Added to My List', 'success');
      } else {
        setWatchlistIds((prev) => {
          const next = new Set(prev);
          next.delete(item.id);
          return next;
        });
        showToast(language === 'he' ? 'הוסר מהרשימה שלי' : 'Removed from My List', 'info');
      }
    } catch {
      showToast(language === 'he' ? 'שגיאה בעדכון הרשימה' : 'Failed to update watchlist', 'error');
    }
  };

  const handleQuickPlay = async (e: React.MouseEvent, item: MediaItem) => {
    e.stopPropagation();
    try {
      const details = await api.getMedia(item.id);
      if (details.files && details.files.length > 0) {
        const file = details.files[0];
        if (file) {
          onPlay(file, item.titleHe || item.title);
        } else {
          onOpenMedia(item.id);
        }
      } else {
        onOpenMedia(item.id);
      }
    } catch {
      onOpenMedia(item.id);
    }
  };

  // Filter & Sort Pipeline
  const filteredItems = useMemo(() => {
    return items
      .filter((item) => {
        // 1. Type
        if (filterType !== 'all' && item.type !== filterType) return false;

        // 2. Mood / Vibe
        if (selectedMood !== 'all') {
          const mood = MOOD_LIST.find((m) => m.id === selectedMood);
          if (mood && mood.genres.length > 0) {
            const itemGenres = (item.genres || []).map((g) => g.toLowerCase());
            const hasMoodMatch = mood.genres.some((mg) => itemGenres.some((ig) => ig.includes(mg)));
            if (!hasMoodMatch) return false;
          }
        }

        // 3. Genre
        if (selectedGenre !== 'all') {
          const itemGenres = (item.genres || []).map((g) => g.toLowerCase());
          if (!itemGenres.some((g) => g.includes(selectedGenre.toLowerCase()))) return false;
        }

        // 3. Year range
        if (selectedYearRange !== 'all') {
          const y = item.year || 0;
          if (selectedYearRange === '2024' && y < 2024) return false;
          if (selectedYearRange === '2020-2023' && (y < 2020 || y > 2023)) return false;
          if (selectedYearRange === '2010s' && (y < 2010 || y > 2019)) return false;
          if (selectedYearRange === '2000s' && (y < 2000 || y > 2009)) return false;
          if (selectedYearRange === 'classic' && (y >= 2000 || y === 0)) return false;
        }

        // 4. Min Rating
        if (minRating > 0) {
          const r = item.rating || 0;
          if (r < minRating) return false;
        }

        // 5. Search query
        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase().trim();
          const matchTitle = (item.title || '').toLowerCase().includes(q);
          const matchHe = (item.titleHe || '').toLowerCase().includes(q);
          const matchOriginal = (item.originalTitle || '').toLowerCase().includes(q);
          const matchCast = (item.cast || []).some((c) => (c.name || '').toLowerCase().includes(q));
          if (!matchTitle && !matchHe && !matchOriginal && !matchCast) return false;
        }

        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'popularity') {
          return (b.voteCount || b.rating || 0) - (a.voteCount || a.rating || 0);
        }
        if (sortBy === 'rating') {
          return (b.rating || 0) - (a.rating || 0);
        }
        if (sortBy === 'year') {
          return (b.year || 0) - (a.year || 0);
        }
        if (sortBy === 'title') {
          const tA = (a.titleHe || a.title || '').toLowerCase();
          const tB = (b.titleHe || b.title || '').toLowerCase();
          return tA.localeCompare(tB);
        }
        return 0;
      });
  }, [items, filterType, selectedMood, selectedGenre, selectedYearRange, minRating, searchQuery, sortBy]);

  const resetFilters = () => {
    setFilterType('all');
    setSelectedMood('all');
    setSelectedGenre('all');
    setSelectedYearRange('all');
    setMinRating(0);
    setSearchQuery('');
    setSortBy('popularity');
  };

  const hasActiveFilters =
    filterType !== 'all' ||
    selectedMood !== 'all' ||
    selectedGenre !== 'all' ||
    selectedYearRange !== 'all' ||
    minRating > 0 ||
    searchQuery.trim() !== '';

  return (
    <div className="min-h-screen pt-28 pb-20 px-4 md:px-10 max-w-7xl mx-auto page-fade-enter">
      {/* Header Title */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 rounded-xl bg-accent-primary/15 text-accent-primary border border-white/10 shadow-accent-glow">
              <Compass className="w-6 h-6 animate-spin-slow" />
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight">
              {language === 'he' ? 'גלה וחקור' : 'Explore & Discover'}
            </h1>
          </div>
          <p className="text-sm text-neutral-400">
            {language === 'he'
              ? 'סנן לפי ז׳אנרים, שנות יציאה, דירוגים וסוג מדיה בכל הספרייה'
              : 'Filter and discover titles across your library by genres, ratings, and years'}
          </p>
        </div>

        {/* Quick Search inside Explore */}
        <div className="relative w-full md:w-80">
          <Search
            className={`absolute ${isRtl ? 'right-3.5' : 'left-3.5'} top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none`}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={language === 'he' ? 'סינון מהיר לפי שם או שחקן...' : 'Filter by title or actor...'}
            dir={isRtl ? 'rtl' : 'ltr'}
            className={`w-full ${isRtl ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2 bg-neutral-900/90 border border-white/10 rounded-xl text-sm text-white placeholder-neutral-500 focus:outline-none focus:border-accent-primary transition-all`}
          />
        </div>
      </div>

      {/* Filter Control Bar */}
      <div className="bg-neutral-950/80 border border-white/10 rounded-2xl p-4 md:p-5 backdrop-blur-xl mb-8 shadow-xl space-y-4">
        {/* Row 1: Content Type & Sort By */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Content Type Tabs */}
          <div className="flex items-center gap-1.5 p-1 bg-neutral-900/90 rounded-xl border border-white/5">
            {[
              { id: 'all', label: language === 'he' ? 'הכול' : 'All', icon: Sparkles },
              { id: 'movie', label: language === 'he' ? 'סרטים' : 'Movies', icon: Film },
              { id: 'show', label: language === 'he' ? 'סדרות' : 'Shows', icon: Tv },
            ].map((tab) => {
              const Icon = tab.icon;
              const active = filterType === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setFilterType(tab.id as any)}
                  className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs md:text-sm font-semibold transition-all cursor-pointer ${
                    active
                      ? 'bg-accent-primary text-white shadow-accent-glow'
                      : 'text-neutral-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Sort By & Reset */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-neutral-400">
              <SlidersHorizontal className="w-3.5 h-3.5" />
              <span>{language === 'he' ? 'מיון:' : 'Sort:'}</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="bg-neutral-900 border border-white/10 rounded-lg px-2.5 py-1 text-xs text-white focus:outline-none focus:border-accent-primary cursor-pointer"
              >
                <option value="popularity">{language === 'he' ? 'הכי פופולרי' : 'Most Popular'}</option>
                <option value="rating">{language === 'he' ? 'דירוג גבוה' : 'Highest Rated'}</option>
                <option value="year">{language === 'he' ? 'שנת יציאה' : 'Release Year'}</option>
                <option value="title">{language === 'he' ? 'שם (A-Z)' : 'Title (A-Z)'}</option>
              </select>
            </div>

            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-neutral-300 text-xs font-medium border border-white/10 transition-colors cursor-pointer"
                title="Reset filters"
              >
                <RotateCcw className="w-3 h-3" />
                <span>{language === 'he' ? 'איפוס' : 'Reset'}</span>
              </button>
            )}
          </div>
        </div>

        {/* Mood & Vibe Selector */}
        <div className="flex items-center gap-2 overflow-x-auto pt-2.5 pb-3 px-1.5 -mx-1.5 -my-1 scrollbar-none">
          {MOOD_LIST.map((m) => {
            const active = selectedMood === m.id;
            return (
              <button
                key={m.id}
                onClick={() => setSelectedMood(m.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer flex items-center gap-1.5 border mood-pill ${
                  active
                    ? 'mood-pill-active z-10'
                    : 'bg-neutral-900 text-neutral-400 border-white/5 hover:text-white hover:bg-neutral-800'
                }`}
              >
                <span>{m.emoji}</span>
                <span>{language === 'he' ? m.labelHe : m.labelEn}</span>
              </button>
            );
          })}
        </div>

        {/* Row 2: Genre Pills */}
        <div className="flex items-center gap-2 overflow-x-auto pt-2 pb-2.5 px-1.5 -mx-1.5 scrollbar-none">
          <button
            onClick={() => setSelectedGenre('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer mood-pill ${
              selectedGenre === 'all'
                ? 'bg-accent-primary text-white shadow-md shadow-accent-glow font-bold z-10'
                : 'bg-neutral-900 text-neutral-400 border border-white/5 hover:text-white hover:bg-neutral-800'
            }`}
          >
            {language === 'he' ? 'כל הז׳אנרים' : 'All Genres'}
          </button>
          {GENRE_LIST.map((genre) => {
            const active = selectedGenre === genre;
            const label = GENRE_TRANSLATIONS[genre]?.[language] || genre;
            return (
              <button
                key={genre}
                onClick={() => setSelectedGenre(genre)}
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer border ${
                  active
                    ? 'bg-accent-primary text-white border-accent-primary shadow-sm'
                    : 'bg-neutral-900/90 text-neutral-400 border-white/5 hover:text-white hover:bg-neutral-800'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Row 3: Year & Min Rating Pills */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-white/5">
          {/* Year Range */}
          <div className="flex items-center gap-1.5 overflow-x-auto">
            <Calendar className="w-3.5 h-3.5 text-neutral-500 mr-1" />
            {[
              { id: 'all', label: language === 'he' ? 'כל השנים' : 'All Years' },
              { id: '2024', label: '2024+' },
              { id: '2020-2023', label: '2020-2023' },
              { id: '2010s', label: '2010s' },
              { id: '2000s', label: '2000s' },
              { id: 'classic', label: language === 'he' ? 'קלאסיקות (<2000)' : 'Classics (<2000)' },
            ].map((y) => (
              <button
                key={y.id}
                onClick={() => setSelectedYearRange(y.id)}
                className={`px-2.5 py-0.5 rounded text-xs transition-colors cursor-pointer ${
                  selectedYearRange === y.id
                    ? 'bg-white/20 text-white font-bold'
                    : 'text-neutral-400 hover:text-neutral-200'
                }`}
              >
                {y.label}
              </button>
            ))}
          </div>

          {/* Rating */}
          <div className="flex items-center gap-1.5">
            <Star className="w-3.5 h-3.5 text-amber-400 mr-1" />
            {[
              { val: 0, label: language === 'he' ? 'הכול' : 'All' },
              { val: 7, label: '7.0+' },
              { val: 8, label: '8.0+' },
              { val: 8.5, label: '8.5+' },
            ].map((r) => (
              <button
                key={r.val}
                onClick={() => setMinRating(r.val)}
                className={`px-2 py-0.5 rounded text-xs transition-colors cursor-pointer ${
                  minRating === r.val
                    ? 'bg-amber-400/20 text-amber-300 font-bold border border-amber-400/40'
                    : 'text-neutral-400 hover:text-neutral-200'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Results Header Count */}
      <div className="flex items-center justify-between mb-5">
        <span className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">
          {language === 'he'
            ? `${filteredItems.length} כותרים נמצאו`
            : `${filteredItems.length} Titles Found`}
        </span>
      </div>

      {/* Grid or Skeleton */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="aspect-[2/3] rounded-xl bg-neutral-900/80 border border-white/5 animate-pulse"
            />
          ))}
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-24 bg-neutral-950/40 rounded-3xl border border-white/5 p-8">
          <Compass className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-white mb-1">
            {language === 'he' ? 'לא נמצאו כותרים מתאימים' : 'No titles found'}
          </h3>
          <p className="text-xs text-neutral-400 mb-6 max-w-sm mx-auto">
            {language === 'he'
              ? 'נסה להרחיב את טווח השנים, לאפס את הז׳אנר או להוריד את סף הדירוג'
              : 'Try broadening the year range, resetting the genre, or lowering the rating'}
          </p>
          <button
            onClick={resetFilters}
            className="px-5 py-2 rounded-xl bg-accent-primary text-white text-xs font-bold shadow-accent-glow hover:bg-accent-primary/80 transition-all cursor-pointer"
          >
            {language === 'he' ? 'איפוס כל המסננים' : 'Reset All Filters'}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {filteredItems.map((item) => {
            const inWatchlist = watchlistIds.has(item.id);
            const isHebrew = language === 'he';
            const displayTitle = isHebrew ? item.titleHe || item.title : item.title || item.titleHe;
            const displaySubtitle = isHebrew ? item.title : item.titleHe;

            return (
              <div
                key={item.id}
                onClick={() => onOpenMedia(item.id)}
                className="group relative aspect-[2/3] rounded-xl overflow-hidden bg-neutral-900 border border-white/5 cursor-pointer hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl hover:border-white/20"
              >
                {/* Poster */}
                <img
                  src={
                    item.posterPath ||
                    'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=800'
                  }
                  alt={displayTitle}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />

                {/* Rating Badge */}
                {item.rating && item.rating > 0 && (
                  <div className="absolute top-2 left-2 flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-black/75 backdrop-blur-md border border-white/10 text-[11px] font-bold text-amber-300">
                    <Star className="w-3 h-3 fill-amber-300 text-amber-300" />
                    <span>{item.rating.toFixed(1)}</span>
                  </div>
                )}

                {/* Media Type Badge */}
                <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded-md bg-black/75 backdrop-blur-md border border-white/10 text-[10px] font-semibold uppercase text-neutral-300">
                  {item.type === 'show' ? 'TV' : 'Movie'}
                </div>

                {/* Hover Quick Actions */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-between p-3">
                  <div className="flex justify-end gap-2 pt-8">
                    <button
                      onClick={(e) => toggleWatchlist(e, item)}
                      className={`p-2 rounded-full backdrop-blur-md border transition-all cursor-pointer ${
                        inWatchlist
                          ? 'bg-emerald-600/90 text-white border-emerald-400'
                          : 'bg-black/60 text-white border-white/20 hover:bg-white/20'
                      }`}
                      title={inWatchlist ? 'In Watchlist' : 'Add to Watchlist'}
                    >
                      {inWatchlist ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={(e) => handleQuickPlay(e, item)}
                      className="p-2 rounded-full bg-accent-primary text-white shadow-accent-glow hover:scale-110 transition-transform cursor-pointer"
                      title="Play"
                    >
                      <Play className="w-3.5 h-3.5 fill-current" />
                    </button>
                  </div>

                  {/* Meta Details */}
                  <div className="space-y-1">
                    <h4 className="text-xs font-bold text-white line-clamp-1 drop-shadow">
                      {displayTitle}
                    </h4>
                    {displaySubtitle && displaySubtitle !== displayTitle && (
                      <p className="text-[10px] text-neutral-300 line-clamp-1 opacity-80">
                        {displaySubtitle}
                      </p>
                    )}
                    <div className="flex items-center gap-2 text-[10px] text-neutral-400 pt-0.5">
                      {item.year && <span>{item.year}</span>}
                      {item.genres?.[0] && (
                        <>
                          <span>•</span>
                          <span className="truncate">{item.genres[0]}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
